// Whether Voolist is being used on a phone or a tablet.
//
// This is about the *device*, not the size of the window. A seller who has made their desktop
// browser narrow is still on a desktop, and treating them as mobile would stop them adding the
// browser extension — so the window's width is deliberately not consulted. Use `useResponsive` for
// layout; use this for what the device can actually do.

// Phones and tablets, from their user agent. Tablets belong here too: Chrome on Android has no
// extensions either, so for our purposes an Android tablet is the same as an Android phone.
const PHONE_OR_TABLET =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|Mobile/i;

/**
 * Whether these browser details describe a phone or tablet.
 *
 * Every rule has to be one a desktop can never satisfy, because a wrong "yes" is far more costly
 * than a wrong "no": it would tell someone at a computer that they cannot do something they can.
 *
 * @param {object} browser
 * @param {string} browser.userAgent `navigator.userAgent`
 * @param {boolean|null} browser.hasMobileHint Chromium's own answer, when the browser gives one
 * @param {number} browser.maxTouchPoints how many fingers the screen tracks; 0 on a plain desktop
 * @returns {boolean}
 */
export function isMobileBrowser ({ userAgent = '', hasMobileHint = null, maxTouchPoints = 0 } = {}) {
  // Chromium tells us directly. Only trusted when it says yes — it reports desktop for some
  // Android tablets, which the user agent below still catches.
  if (hasMobileHint === true) return true;

  if (PHONE_OR_TABLET.test(userAgent)) return true;

  // An iPad on iPadOS 13 or later calls itself a Mac, so its touchscreen is the only thing that
  // gives it away. Kept to Macs on purpose: a Windows laptop with a touchscreen reports plenty of
  // touch points and is not a tablet.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return true;

  return false;
}

/**
 * Whether this seller is on a phone or tablet right now.
 * @returns {boolean}
 */
export function isMobileDevice () {
  if (typeof navigator === 'undefined') return false;

  return isMobileBrowser({
    userAgent: navigator.userAgent || '',
    hasMobileHint: navigator.userAgentData?.mobile ?? null,
    maxTouchPoints: navigator.maxTouchPoints || 0,
  });
}
